/**
 * Pipeline/status visualization (prototype `importDetailPipeline`, 3626–3651):
 * the parse-status indicator, plus the conflicts bar when
 * `unresolved_conflicts > 0`.
 *
 * The status labels are FE-side (the wire carries the short parse-status keys
 * per the canonical vocabulary). The tag pills that used to sit beside the
 * status were removed for Trello #711 cl.4 — see the note below. Tokens only.
 */
import type { ImportDetail, ImportStatus } from "@nu-assist/contracts";

const STATUS_LABEL: Record<ImportStatus, string> = {
  uploaded: "Uploaded",
  parsing: "Parsing…",
  ready: "Ready",
  failed: "Failed",
};

const STATUS_CLASS: Record<ImportStatus, string> = {
  uploaded: "bg-surface text-text-secondary",
  // `-ink` for the TEXT, the base token for the fill — the rule `tokens.css`
  // states where `--accent-ink` is defined. These three paired a muted fill
  // with the BASE colour and measured 1.50 / 1.35 / 2.61 in light mode; the
  // ink family puts them at 4.82 / 5.11 / 5.16.
  parsing: "bg-warning-muted text-warning-ink",
  ready: "bg-accent-muted text-accent-ink",
  failed: "bg-error-muted text-error-ink",
};

// The tag-pill row is GONE (Trello #711 cl.4). It used to render one pill per
// tag — "Raw Import, Exported" — which mixed two questions: what this list IS
// (now the type badge in the page header) and what has HAPPENED to it. Jared
// asked for the "Exported" chip to go because persistent lifecycle state is
// clutter next to a progress bar saying the same thing; the type half moved
// somewhere more prominent rather than being deleted. `TAG_DEFS` is no longer
// read here — the header owns that lookup now.
//
// WHAT THIS COSTS, recorded because the comment this replaced argued the other
// way ("this page is where the reader came to see everything true about ONE
// list") and /pr-review iter 1 (Agent C I4) was right to make us answer it:
//
//   - `exported` keeps a surface — the Contacts card's exported stat line, and
//     the per-contact "Added to search" column. Nothing is lost.
//   - `enriched` and `combined` now have NO surface anywhere in the product.
//     The list page collapsed to a single type pill in cl.1; this was their
//     last one. `enriched` is actively set by the export worker, so this is a
//     real (small) loss of visibility, not a dead value.
//
// Accepted deliberately rather than by oversight: both are ACTIONS ON a list,
// which is the exact distinction Jared spent the 2026-08-18 call drawing, and
// re-adding a chip row here to carry them would restore the confusion cl.4
// removed. If enrichment state needs a home, it belongs with the enrichment
// data (the contacts table already has an "Enrichment failed" filter), not as
// a list-level chip. The `?tag=enriched` / `?tag=combined` query plumbing is
// untouched, so nothing is foreclosed.

export function PipelineStatus({ detail }: { detail: ImportDetail }) {
  const conflicts = detail.conflict_count ?? 0;
  // `ready` is the resting state of every list a recruiter ever opens, so a
  // pill saying so is a label on the absence of news — the same clutter the
  // "Exported" chip was removed for. The pill speaks only when there IS
  // something to say: still uploading, still parsing, or the parse failed.
  //
  // The pill is HIDDEN for `ready`, not emptied — an empty coloured pill reads
  // as a broken one. Every other state keeps it, which matters because this
  // span is the only place in the product a failed parse is visible and
  // `upload-parse-list.spec.ts` asserts `data-status="failed"` through it.
  //
  // With no pill and no conflicts there is NOTHING to render, and the
  // component returns null rather than an empty container. That is not tidiness:
  // this sits inside the page's `flex flex-col gap-4`, where a zero-height child
  // is still a flex item, so an empty `<div>` doubled the space between the info
  // grid and what follows to 32px on every list in its resting state — which is
  // every list a recruiter opens (`/pr-review` #771 iter 1, Agent C).
  //
  // So `pipeline-status` is NOT a mount anchor — it is absent in the common
  // case. Anything waiting for the page to have data should wait on something
  // that always renders once the detail query resolves, like `contacts-table`.
  //
  // `STATUS_LABEL` keeps its `ready` entry: it is a `Record<ImportStatus,
  // string>`, so deleting the key is a type error, and the string is still the
  // right answer to "what is this status called" for anything that asks later.
  const label = detail.status === "ready" ? null : STATUS_LABEL[detail.status];

  // Derived here rather than in the JSX so the render stays declarative, and
  // so "nothing was lost" is a single 0 rather than a shape check at two sites.
  const discarded = detail.discarded_dates ?? {};
  const discardedTotal = Object.values(discarded).reduce((a, b) => a + b, 0);
  const discardedColumns = Object.keys(discarded).join(", ");

  if (label === null && conflicts === 0 && discardedTotal === 0) return null;

  return (
    <div data-testid="pipeline-status" className="flex flex-col gap-3">
      {label !== null && (
        <div className="flex flex-wrap items-center gap-2">
          <span
            data-testid="parse-status"
            data-status={detail.status}
            className={`inline-flex items-center rounded-pill px-3 py-1 text-caption ${STATUS_CLASS[detail.status]}`}
          >
            {label}
          </span>
        </div>
      )}

      {/* The reason, when the failure has a knowable one. Until this landed the
          pill above was the only thing a failed parse rendered, so a file
          rejected for a reason we could name looked exactly like one that died
          on an internal error. Absent rather than empty when there is nothing
          to say — an empty box reads as "we know something and won't tell".
          Not for a combined list: the page shows its own single
          "Combining stopped" notice there (`combined-failed-notice`), and
          `parse_error` for it is a worker/sweeper line, not a reason a
          recruiter can act on — two failure messages for one failure
          (/pr-review iter 1+2, Agent C M7). */}
      {detail.status === "failed" && detail.parse_error && detail.import_format !== "combined" ? (
        <p data-testid="parse-error-reason" className="text-body text-text-secondary">
          {detail.parse_error}
        </p>
      ) : null}

      {/* A successful parse that quietly dropped data. `ready` used to mean
          only "the file parsed", never "we read all of it": one 690-contact
          import lost 4,190 of its 4,883 start dates and reported success, and
          the gap surfaced days later as blank tenure in the Company Map. The
          total is what tells a recruiter whether to care; the column names are
          what tell an engineer where to look. */}
      {discardedTotal > 0 ? (
        <p data-testid="discarded-dates-warning" className="text-body text-warning-ink">
          {discardedTotal.toLocaleString()} date
          {discardedTotal === 1 ? "" : "s"} in this file could not be read and were left empty (
          {discardedColumns}). The contacts imported; their dates did not.
        </p>
      ) : null}

      {conflicts > 0 && (
        // Informational only — the canonical "Compare to Thrive" CTA now lives
        // in the Search card below (Trello #711 cl.4; it was in the page header
        // before). A second button here was never wired → removed.
        <div
          data-testid="conflicts-bar"
          className="flex items-center rounded-md border border-border bg-warning-muted px-4 py-2"
        >
          <span className="text-body text-warning-ink">
            {/* "to review", not "unresolved conflicts with Thrive". The
                population it counts is broader than the word: it includes
                company changes, rows carrying only a duplicate-URL warning, and
                W3 name matches — none of which is a conflict WITH Thrive, and
                the first of which is the ordinary case. The list's pill and the
                table's chip now say the same two words over the same number. */}
            {conflicts.toLocaleString()} contact
            {conflicts === 1 ? "" : "s"} to review. Use “Compare to Thrive” in the Search card to
            review them.
          </span>
        </div>
      )}
    </div>
  );
}
